import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Sun, Moon, Search, ChevronDown, Lock, Church, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { ScrollHint } from "@/components/ScrollHint";

const ThemeToggle = ({ className = "" }) => {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className={`flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-primary/60 hover:text-primary ${className}`}
    >
      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
};

const COURSES = [
  { to: "/instruments/pianists", label: "Piano", img: "/media/jt-grand-piano.jpg", sub: "Read music at the keyboard, from Middle C to chords" },
  { to: "/instruments/guitarists", label: "Guitar", img: "https://images.unsplash.com/photo-1588450523206-e0b048d8f4d3?crop=entropy&cs=srgb&fm=jpg&q=60&w=160", sub: "Stave and TAB side by side, chords and strumming" },
  { to: "/instruments/drummers", label: "Drums", img: "https://images.unsplash.com/photo-1503506584117-5ed92741077a?crop=entropy&cs=srgb&fm=jpg&q=60&w=160", sub: "Drum notation, grooves and fills" },
  { to: "/instruments/singers", label: "Voice", img: "https://images.unsplash.com/photo-1522863602463-afebb8886ab2?crop=entropy&cs=srgb&fm=jpg&q=60&w=160", sub: "Sight-sing, find your range and sing in parts" },
  { to: "/instruments/pianists#gospel", label: "Gospel Piano", gospel: true, note: "After a normal course", sub: "Beginner & Intermediate, 6 lessons each" },
  { to: "/instruments/guitarists#gospel", label: "Gospel Guitar", gospel: true, note: "After a normal course", sub: "Beginner & Intermediate, 6 lessons each" },
];
const TRAINING = [
  { to: "/articles", label: "Theory Articles" },
  { to: "/trainer", label: "Sight-Reading Trainer" },
  { to: "/quizzes", label: "Quizzes" },
  { to: "/daily-challenge", label: "Daily Challenge" },
  { to: "/ear-trainer", label: "Melody Ear Trainer" },
  { to: "/rhythm-clap", label: "Rhythm Clap Game" },
  { to: "/choir-practice", label: "Choir Part Practice" },
  { to: "/worksheets", label: "Worksheet Library" },
  { to: "/terms", label: "My Musical Terms" },
  { to: "/hall-of-fame", label: "Leaderboard" },
];
const SIMPLE = [
  { to: "/about", label: "About" },
  { to: "/pricing", label: "Pricing" },
  { to: "/why-choose", label: "Why Choose" },
  { to: "/faqs", label: "FAQs" },
  { to: "/community", label: "Community" },
  { to: "/more", label: "More from Jason" },
];

const TIPS = {
  About: "Meet Jason: his story, experience and teaching",
  Training: "Free practice tools: sight-reading, quizzes, ear training and flashcards",
  Courses: "Voice, guitar, drums and piano courses",
  Pricing: "Course prices, bundles, 1:1 lessons and class licences",
  "Why Choose": "Why The JT Hub works better than random videos",
  FAQs: "Answers to the most common questions",
  Community: "Students, leaderboards and testimonials",
  "More from Jason": "Courses coming soon, Jason's biography and booking the JT Band",
  Payment: "Pay for courses, 1:1 sessions, Gospel courses and class licences",
  "Class Teaching": "Your classes, students and teaching tools",
  Admin: "Admin console",
};

const linkCls = (active) =>
  `whitespace-nowrap rounded-full px-1.5 py-2 text-[13px] font-medium transition-colors ${
    active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
  }`;

const MobileLink = ({ l, onClose, sub }) => (
  <Link to={l.to} onClick={onClose} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
    className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary ${sub ? "pl-7" : ""}`}>
    <span className="min-w-0">{l.label}{l.sub && sub && <span className="block text-[11px] text-muted-foreground/80">{l.sub}</span>}</span>
    {l.note && <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">{l.note}</span>}
  </Link>
);

const MobileGroup = ({ label, items, onClose }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`mobile-nav-${label.toLowerCase()}-toggle`}
        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary">
        {label} <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div data-testid={`mobile-nav-${label.toLowerCase()}-items`}>{items.map((l, k) => (
        <React.Fragment key={l.to}>
          {l.gospel && !items[k - 1]?.gospel && <p className="ml-7 mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary" data-testid="mobile-nav-gospel-note"><Lock size={12} /> Gospel courses: only available after buying a normal course</p>}
          <MobileLink l={l} onClose={onClose} sub />
        </React.Fragment>
      ))}</div>}
    </div>
  );
};

const Dropdown = ({ label, items, loc }) => {
  const [open, setOpen] = useState(false);
  const active = items.some((i) => loc.pathname.startsWith(i.to));
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button title={TIPS[label]} data-testid={`nav-${label.toLowerCase()}-dropdown`} className={`${linkCls(active)} inline-flex items-center gap-1`}>
        {label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute left-0 top-full z-50 pt-2 ${items[0].sub ? "w-96" : "w-60"}`}>
          <div className="overflow-hidden rounded-2xl border border-border bg-popover/95 p-1.5 shadow-2xl backdrop-blur-xl">
            {items.map((i, k) => i.sub ? (
              <React.Fragment key={i.to}>
                {i.gospel && !items[k - 1]?.gospel && (
                  <p className="mx-2 mt-2 flex items-center gap-1.5 border-t border-border px-1 pt-2.5 text-[11px] font-bold uppercase tracking-wider text-primary" data-testid="nav-dd-gospel-note">
                    <Lock size={12} /> Gospel courses: only available after buying a normal course
                  </p>
                )}
                <Link to={i.to} data-testid={`nav-dd-${i.label.toLowerCase().replace(/ /g, "-")}`}
                  className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm text-foreground transition-colors hover:bg-primary/10">
                  {i.img ? <img src={i.img} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
                    : <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"><Church size={18} /></span>}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 font-semibold">{i.label}{i.note && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">{i.note}</span>}</span>
                    <span className="block text-xs text-muted-foreground">{i.sub}</span>
                  </span>
                </Link>
              </React.Fragment>
            ) : (
              <Link
                key={i.to}
                to={i.to}
                data-testid={`nav-dd-${i.label.toLowerCase()}`}
                className="flex items-center justify-between rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                {i.label}
                {i.note && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{i.note}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Phone/tablet bubble beside the logo, pointing right at the Menu button, shown briefly on every page.
const MenuHint = ({ onClose }) => (
  <div className="relative mr-1 flex max-w-[10rem] items-center rounded-2xl bg-primary py-1 pl-3 pr-0.5 text-xs font-semibold leading-tight text-primary-foreground shadow-lg xl:hidden" data-testid="mobile-menu-hint">
    <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 bg-primary" />
    <span className="relative animate-pulse">Tap Menu for more pages</span>
    <button onClick={onClose} aria-label="Dismiss" data-testid="mobile-menu-hint-close" className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-black/10"><X size={15} /></button>
  </div>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [showHint, setShowHint] = useState(true);
  const dismissHint = () => setShowHint(false);
  useEffect(() => {
    setShowHint(true);
    const t = setTimeout(() => setShowHint(false), 8000);
    return () => clearTimeout(t);
  }, [loc.pathname]);

  const [open, setOpen] = useState(false);

  const staffLinks = [];
  staffLinks.push({ to: user && (user.role === "teacher" || user.role === "admin") ? "/teacher" : "/class-teaching", label: "Class Teaching" });
  staffLinks.push({ to: "/more", label: "More from Jason" });
  staffLinks.push({ to: "/payment", label: "Payment" });
  if (user && user.role === "admin") staffLinks.push({ to: "/admin", label: "Admin" });

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (cmdK || (e.key === "/" && !typing)) {
        e.preventDefault();
        nav("/search");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  const allMobile = [
    { to: "/about", label: "About" },
    { to: "/why-choose", label: "Why Choose" },
    { to: "/faqs", label: "FAQs" },
    { to: "/community", label: "Community" },
    { to: "/keep-in-touch", label: "Let's keep in touch" },
    { group: "Courses", items: COURSES },
    staffLinks[0],
    { to: "/pricing", label: "Pricing" },
    { group: "Training", items: TRAINING },
    ...staffLinks.slice(1),
  ];

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 sm:px-8 xl:px-4 py-2.5 2xl:max-w-[1400px]">
        <Link to="/" className="group shrink-0" title="Home" aria-label="Home" data-testid="brand-link">
          <Logo height={68} className="dark:shadow-sm" />
        </Link>

        <div className="hidden items-center gap-0.5 xl:flex">
          <Link to="/about" data-testid="nav-about" title={TIPS["About"]} className={linkCls(loc.pathname.startsWith("/about"))}>About</Link>
          <Link to="/why-choose" data-testid="nav-why-choose" title={TIPS["Why Choose"]} className={linkCls(loc.pathname.startsWith("/why-choose"))}>Why Choose</Link>
          <Link to="/faqs" data-testid="nav-faqs" title={TIPS["FAQs"]} className={linkCls(loc.pathname.startsWith("/faqs"))}>FAQs</Link>
          <Link to="/community" data-testid="nav-community" title={TIPS["Community"]} className={linkCls(loc.pathname.startsWith("/community"))}>Community</Link>
          <Link to="/keep-in-touch" data-testid="nav-keep-in-touch" title="Let's keep in touch: join the mailing list for offers and updates" aria-label="Let's keep in touch" className={`${linkCls(loc.pathname.startsWith("/keep-in-touch"))} inline-flex items-center gap-1`}><Mail size={15} className="2xl:hidden" /><span className="hidden 2xl:inline">Let's keep in touch</span></Link>
          <Dropdown label="Courses" items={COURSES} loc={loc} />
          {staffLinks.map((l, i) => (
            <React.Fragment key={l.to}>
              <Link to={l.to} title={TIPS[l.label]} data-testid={`nav-${l.to.slice(1)}`} className={linkCls(loc.pathname.startsWith(l.to))}>{l.label}</Link>
              {i === 0 && <>
                <Link to="/pricing" data-testid="nav-pricing" title={TIPS["Pricing"]} className={linkCls(loc.pathname.startsWith("/pricing"))}>Pricing</Link>
                <Dropdown label="Training" items={TRAINING} loc={loc} />
              </>}
            </React.Fragment>
          ))}
        </div>

        <div className="hidden items-center gap-1.5 xl:flex 2xl:gap-2">
          <Link to="/search" data-testid="nav-search" aria-label="Search lessons"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-primary/60 hover:text-primary">
            <Search size={16} />
          </Link>
          <ThemeToggle />
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard-button">
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">My Dashboard</Button>
              </Link>
              <button onClick={doLogout} data-testid="logout-button" aria-label="Log out"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-primary/60 hover:text-primary">
                <LogOut size={15} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="rounded-full text-foreground hover:bg-secondary">Log in</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Get started</Button>
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <ThemeToggle />
          {showHint && !open && <MenuHint onClose={dismissHint} />}
          <button className="relative flex flex-col items-center text-foreground" onClick={() => { setOpen(!open); dismissHint(); }} aria-label="Open menu for more pages" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
            <span className="text-[10px] font-semibold uppercase leading-none tracking-wider">Menu</span>
          </button>
        </div>
      </nav>

      {open && (
        <div className="max-h-[75vh] overflow-y-auto border-t border-border bg-background px-4 py-3 xl:hidden">
          <Link to="/search" onClick={() => setOpen(false)} data-testid="mobile-nav-search"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary">
            <Search size={15} /> Search lessons
          </Link>
          {allMobile.map((l) => l.group
            ? <MobileGroup key={l.group} label={l.group} items={l.items} onClose={() => setOpen(false)} />
            : <MobileLink key={l.to + l.label} l={l} onClose={() => setOpen(false)} />)}
          <div className="mt-2 flex gap-2">
            {user ? (
              <>
                <Link to="/dashboard" className="flex-1" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-primary text-primary-foreground">My Dashboard</Button>
                </Link>
                <Button onClick={doLogout} variant="outline" className="rounded-full">Log out</Button>
              </>
            ) : (
              <>
                <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full">Log in</Button>
                </Link>
                <Link to="/register" className="flex-1" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-primary text-primary-foreground">Sign up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
    <ScrollHint />
    <PageBack />
    </>
  );
};

const PageBack = () => {
  const { pathname } = useLocation();
  if (pathname === "/" || /^\/(instruments\/|courses\/[^/]+\/|lessons\/|practice-paper|terms|lyrics)/.test(pathname)) return null;
  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-8" data-testid="page-back-bar">
      <BackButton testId="page-back-button" />
    </div>
  );
};

export default Navbar;
